import _ from "lodash";
import moment from "moment";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MediaFile } from "src/api/file/file-types";
import { BaseDatePicker, BaseTextInput } from "src/components";
import BaseFileUpload from "src/components/BaseFileUpload";

import { emailFormat, linePhoneFormat, onlyNumber } from "src/utils/common-util";

// 세금계산서 정보 컴포넌트의 Props 타입 정의
type Props = {
  mode: string; // 컴포넌트 모드
  documentProofRequest: any; // 증빙서류 요청 정보
  setDocumentProofRequest: any; // 증빙서류 요청 정보 설정 함수
  proofError: any; // 증빙서류 에러 정보
  setProofError: any; // 증빙서류 에러 정보 설정 함수
};

// 세금계산서 정보 컴포넌트
// 세금계산서 발행을 위한 정보를 입력받는 컴포넌트
const TaxInvoiceInfo = ({ mode, documentProofRequest, setDocumentProofRequest, proofError, setProofError }: Props) => {
  const { t } = useTranslation("contract");
  //사업자/법인 등록번호 검증
  const checkValidateOrgNumber = useCallback((number: string) => {
    if (!number) return false;
    const cleanNumber = number?.replace(/-/gi, "");
    const numberMap: number[] = cleanNumber.split("").map((d) => parseInt(d, 10));
    // 10자리(사업자등록번호) 또는 13자리(법인등록번호) 검증
    if (numberMap.length === 10 || numberMap.length === 13) {
      return true;
    }
    return false;
  }, []);

  // 컴포넌트 마운트 시 증빙서류 타입 초기화
  useEffect(() => {
    const check = _.cloneDeep(documentProofRequest);
    if (check.expenseProofType === null) {
      check.expenseProofType = "TAX_INVOICE";
      check.identityType = "CORP_NUM";
      setDocumentProofRequest(check);
    }
  }, [documentProofRequest, setDocumentProofRequest, proofError]);

  // 미디어 파일 목록이 비어있는지 확인
  const isMediaListEmpty = useMemo(() => {
    let rtn = false;
    const mlist = documentProofRequest.mediaList.filter((media: any) => media.isDeleted === false && media.cmdType !== "D");
    if (mlist.length === 0) {
      rtn = true;
    }
    return rtn;
  }, [documentProofRequest]);

  return (
    <div className="w-100">
      <article>
        {/* 세금계산서 정보 입력 섹션 */}
        <section className="tax-calculate">
          <div className="container">
            {/* 안내 메시지 */}
            <ul className="base-list mb20">
              <li>
                {t("detail.taxInvoiceIssuanceNotice")
                  .split("\n")
                  .map((line, idx) => (
                    <Fragment key={idx}>
                      {idx > 0 && <br />}
                      {line}
                    </Fragment>
                  ))}
              </li>
            </ul>

            {/* 사업자 등록번호 입력 필드 */}
            <p className="index-title required">{t("detail.businessRegistrationNumber")}</p>
            <BaseTextInput
              value={documentProofRequest.identityNum}
              onChange={(value: string | undefined) => {
                setDocumentProofRequest({
                  ...documentProofRequest,
                  identityNum: onlyNumber(String(value)),
                });
                if (value) {
                  setProofError({
                    ...proofError,
                    error: { ...proofError.error, identityNum: false },
                    format: { ...proofError.format, identityNum: false },
                  });
                }
              }}
              isInvalid={documentProofRequest.identityNum === "" || documentProofRequest.identityNum === null}
              placeholder={t("detail.businessRegistrationNumberPlaceholder")}
              onBlur={() => {
                const identityNum = documentProofRequest.identityNum;
                const isEmpty = !identityNum || identityNum === "";

                setProofError({
                  ...proofError,
                  error: { ...proofError.error, identityNum: isEmpty },
                  format: { ...proofError.format, identityNum: isEmpty ? false : !checkValidateOrgNumber(identityNum) },
                });
              }}
            />
            {proofError.error.identityNum && <p className="mt5 font12 text-red">{t("detail.businessRegistrationNumberRequired")}</p>}
            {proofError.format.identityNum && <p className="mt5 font12 text-red">{t("detail.businessRegistrationNumberInvalid")}</p>}
          </div>

          {/* 사업자 등록증 사본 업로드 필드 */}
          <div className="container ">
            <p className="index-title required">{t("detail.copyOfBusinessRegistration")}</p>
            <BaseFileUpload
              type="PROOF"
              serviceTag={"BUSINESS_LICENSE"}
              isOnlyView={false}
              limit={1}
              fileList={documentProofRequest?.mediaList ?? []}
              setFiles={(files: MediaFile[]) => {
                // documentProofRequest 값을 최신값을 불러오지 못함. prev 값으로 처리
                setDocumentProofRequest((prev: any) => {
                  const next = _.cloneDeep(prev);
                  next.mediaList = [...(files as any[])];
                  next.mediaList.forEach((mediaFile: any) => {
                    mediaFile["orderNums"] = 1;
                    if (next.contractId && Number(next.contractId) > 0 && (mediaFile.isDeleted || mediaFile.isRemoved)) {
                      mediaFile["cmdType"] = "D";
                    } else {
                      mediaFile["cmdType"] = "C";
                    }
                    mediaFile["isPrimary"] = true;
                  });

                  return next;
                });
                const err = _.cloneDeep(proofError);
                err.error.mediaList = false;

                setProofError(err);
              }}
            />
            {(proofError.error.mediaList || isMediaListEmpty) && <p className="mt5 font12 text-red">{t("detail.businessRegistrationAttachRequired")}</p>}
          </div>

          {/* 수신 이메일 입력 필드 */}
          <div className="container">
            <p className="index-title required">{t("detail.recipientEmail")}</p>
            <BaseTextInput
              value={documentProofRequest.taxInvoiceEmail}
              onChange={(value: string | undefined) => {
                setProofError({
                  ...proofError,
                  error: { ...proofError.error, taxInvoiceEmail: false },
                  format: { ...proofError.format, taxInvoiceEmail: false },
                });

                const hasKorean = /[ㄱ-ㅣ가-힣]/.test(String(value));
                if (!hasKorean) {
                  setDocumentProofRequest({
                    ...documentProofRequest,
                    taxInvoiceEmail: value,
                  });
                }
              }}
              onBlur={() => {
                const taxInvoiceEmail = documentProofRequest.taxInvoiceEmail;
                const isValid = emailFormat(taxInvoiceEmail);

                setProofError({
                  ...proofError,
                  format: { ...proofError.format, taxInvoiceEmail: !isValid },
                });
              }}
              isInvalid={documentProofRequest.taxInvoiceEmail === "" || documentProofRequest.taxInvoiceEmail === null}
              placeholder={t("detail.emailPlaceholder")}
            />

            {proofError.format.taxInvoiceEmail && <p className="mt5 font12 text-red">{t("detail.emailInvalid")}</p>}
          </div>

          {/* 유선 전화번호 입력 필드 */}
          <div className="container">
            <p className="index-title required">{t("detail.landlineNumber")}</p>
            <BaseTextInput
              value={documentProofRequest.taxInvoicePhone}
              onChange={(value: string | undefined) => {
                const temp = _.cloneDeep(documentProofRequest);
                temp.taxInvoicePhone = onlyNumber(String(value));
                setDocumentProofRequest(temp);
                if (value) {
                  const err = _.cloneDeep(proofError);
                  err.error.taxInvoicePhone = false;
                  err.format.taxInvoicePhone = false;
                  setProofError(err);
                }
              }}
              onBlur={() => {
                const temp = _.cloneDeep(documentProofRequest);
                const pf = _.cloneDeep(proofError);
                if (linePhoneFormat(temp.taxInvoicePhone)) {
                  pf.format.taxInvoicePhone = false;
                } else {
                  pf.format.taxInvoicePhone = true;
                }
                setProofError(pf);
              }}
              isInvalid={documentProofRequest.taxInvoicePhone === "" || documentProofRequest.taxInvoicePhone === null}
              placeholder={t("detail.landlineNumberPlaceholder")}
            />
            {proofError.format.taxInvoicePhone && <p className="mt5 font12 text-red">{t("detail.landlineNumberInvalid")}</p>}
          </div>

          {/* 계산서 발행일자 선택 필드 */}
          <div className="container">
            <p className="index-title required">{t("detail.invoiceIssueDate")}</p>
            <BaseDatePicker
              value={documentProofRequest.taxInvoiceDate ? moment(documentProofRequest.taxInvoiceDate).toDate() : null}
              className="text-left"
              onChange={(value: Date) => {
                const temp = _.cloneDeep(documentProofRequest);
                temp.taxInvoiceDate = value;
                setDocumentProofRequest(temp);
                if (value) {
                  const err = _.cloneDeep(proofError);
                  err.error.taxInvoiceDate = false;
                  err.format.taxInvoiceDate = false;
                  setProofError(err);
                }
              }}
              placeholder={t("detail.invoiceIssueDatePlaceholder")}
            />
            {proofError.error.taxInvoiceDate && <p className="mt5 font12 text-red">{t("detail.invoiceIssueDateRequired")}</p>}
          </div>
        </section>
      </article>
    </div>
  );
};

export default TaxInvoiceInfo;
